import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'

export function useFormatRelativeTime() {
  const { t, locale } = useI18n()

  function format(ts: number | undefined | null): string | undefined {
    if (!ts || !Number.isFinite(ts)) {
      return undefined
    }

    const dt = DateTime.fromMillis(Number(ts)).setLocale(locale.value)
    if (!dt.isValid) {
      return undefined
    }

    const now = DateTime.local().setLocale(locale.value)

    const minutesDiff = Math.max(0, Math.floor(now.diff(dt, 'minutes').minutes ?? 0))
    if (minutesDiff < 5) {
      return t('time.relative.recently') || 'Recently'
    }

    if (now.hasSame(dt, 'day')) {
      const hoursRel = dt.toRelative({ base: now, unit: 'hours', round: true })
      if (hoursRel && Math.abs(now.diff(dt, 'hours').hours ?? 0) >= 1) {
        return hoursRel
      }
      const minutesRel = dt.toRelative({ base: now, unit: 'minutes', round: true })
      if (minutesRel) {
        return minutesRel
      }
    }

    const calRel = dt.toRelativeCalendar({ base: now })
    if (calRel) {
      return calRel
    }

    if (now.hasSame(dt, 'year')) {
      return dt.toLocaleString({ month: 'short', day: 'numeric' })
    }
    return dt.toLocaleString(DateTime.DATE_MED)
  }

  function formatTitle(ts: number | undefined | null): string | undefined {
    if (!ts || !Number.isFinite(ts)) {
      return undefined
    }
    const dt = DateTime.fromMillis(Number(ts))
    if (!dt.isValid) {
      return undefined
    }
    return dt.setLocale(locale.value).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
  }

  function formatDateTimeAttr(ts: number | undefined | null): string | undefined {
    if (!ts || !Number.isFinite(ts)) {
      return undefined
    }
    const dt = DateTime.fromMillis(Number(ts))
    if (!dt.isValid) {
      return undefined
    }
    return dt.toISO() ?? undefined
  }

  return { format, formatTitle, formatDateTimeAttr }
}
